import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { LocationDetailForm } from "@/features/location/detail/LocationDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { PartnerDetailForm } from "@/features/partner/form/detailForm";
import { PurchaseOrderItemsDetailForm } from "@/features/purchaseOrderItems/form/purchaseOrderItemDetailForm";
import { DetailBadge } from "@/features/ui/detail/DetailBadge";
import { DetailContent } from "@/features/ui/detail/DetailContent";
import { DetailField } from "@/features/ui/detail/DetailField";
import { DetailHeader } from "@/features/ui/detail/DetailHeader";
import { DetailInformTitle } from "@/features/ui/detail/DetailInformTitle";
import { DetailLabel } from "@/features/ui/detail/DetailLabel";
import { DetailLink } from "@/features/ui/detail/DetailLink";
import { DetailLinkWrapper } from "@/features/ui/detail/DetailLinkWrapper";
import { DetailTitle } from "@/features/ui/detail/DetailTitle";
import { setStockPlanStatus, stockPlanStatusColor } from "@/utils/scmDataStatus";
import timeUtil from "@/utils/timeUtil";
import { Badge, Checkbox, Space } from "@mantine/core";
import { StockPlansGet200ResponseRowsInner } from "@sizlcorp/sizl-api-document/dist/models";
import { useTranslation } from "react-i18next";

const DetailStockPlan = Object.assign(DetailHeader, {
  Title: DetailTitle,
  Inform: DetailInformTitle,
  Field: DetailField,
  Label: DetailLabel,
  Link: DetailLink,
  LinkWrapper: DetailLinkWrapper,
  Badge: DetailBadge,
  Content: DetailContent,
  CheckBox: Checkbox,
});

export interface DetailStockPlanInformProps {
  data: StockPlansGet200ResponseRowsInner | undefined;
}

export const StockPlanDetailInForm = (params: DetailStockPlanInformProps) => {
  const { data } = params;
  const { t } = useTranslation();
  const { openModal } = useModal();

  return (
    <>
      <DetailStockPlan>
        <DetailStockPlan.Title>
          {t("common.detail", {
            item: t("stockPlan.stockPlan"),
          })}
        </DetailStockPlan.Title>
        <Space h={8} />
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.status")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            <Badge
              variant="filled"
              color={stockPlanStatusColor[data?.status as keyof typeof stockPlanStatusColor]}
            >
              {t(setStockPlanStatus(data?.status ?? ""))}
            </Badge>
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.code")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>{data?.code}</DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.direction")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            <Badge color={data?.direction === "INBOUND" ? "blue" : "red"}>
              {t(data?.direction ?? "")}
            </Badge>
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("partner.name")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            <DetailStockPlan.Link
              onClick={() => {
                openModal(
                  <PartnerDetailForm partnerCode={data?.partnerCode} />,
                  null,
                  t("common.detail", { item: t("partner.partner") }), // 파트너 상세
                );
              }}
            >
              {data?.partner?.name}
            </DetailStockPlan.Link>
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("item.name")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            <DetailStockPlan.Link
              onClick={() => {
                openModal(
                  <ItemsDetailForm itemCode={data?.itemCode} />,
                  null,
                  t("common.detail", { item: t("item.item") }), // 아이템 상세
                );
              }}
            >
              {data?.itemName}
            </DetailStockPlan.Link>
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.quantity")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>{data?.quantity}</DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.sumOfActualQuantity")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>{data?.sumOfActualQuantity}</DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.availableActualQuantity")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>{data?.availableActualQuantity}</DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.scheduledAt")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>{timeUtil(data?.scheduledAt ?? "")}</DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.sourceLocation")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            <DetailStockPlan.Link
              onClick={() => {
                openModal(
                  <LocationDetailForm locationCode={data?.sourceLocationCode} />,
                  null,
                  t("common.detail", { item: t("location.location") }), // 위치 상세
                );
              }}
            >
              {data?.sourceLocationName}
            </DetailStockPlan.Link>
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("stockPlan.targetLocation")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            <DetailStockPlan.Link
              onClick={() => {
                openModal(
                  <LocationDetailForm locationCode={data?.targetLocationCode} />,
                  null,
                  t("common.detail", { item: t("location.location") }), // 위치 상세
                );
              }}
            >
              {data?.targetLocationName}
            </DetailStockPlan.Link>
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("lot.info")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            {data?.lotData
              ? `ID: ${data.lotData.id} | 로트명: ${data.lotData.name ?? "-"} | 유효기한: ${data.lotData.expiration !== null ? timeUtil(data.lotData.expiration ?? "") : "-"}`
              : "-"}
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("order.manager")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>{data?.manager}</DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>
            {t("purchaseOrderItems.purchaseOrderItems")}
          </DetailStockPlan.Label>
          <DetailStockPlan.Content>
            {data?.purchaseOrderItemId ? (
              <DetailStockPlan.Link
                onClick={() => {
                  openModal(
                    <PurchaseOrderItemsDetailForm
                      purchaseOrderItemId={data?.purchaseOrderItemId ?? 0}
                      // pageKey={
                      //   data?.direction === "INBOUND"
                      //     ? "purchaseOrderItems"
                      //     : "orderItems"
                      // }
                      pageKey="purchaseOrderItems"
                    />,
                    null,
                    t("common.detail", { item: t("item.item") }),
                  );
                }}
              >
                {data.purchaseOrderItemId}
              </DetailStockPlan.Link>
            ) : (
              "-"
            )}
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("info.creatorUserCode")}</DetailStockPlan.Label>
          <DetailStockPlan.Content> {data?.creatorUserName ?? ""}</DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("info.createdAt")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            {data?.createdAt ? timeUtil(data?.createdAt ?? "") : ""}
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("info.updaterUserCode")}</DetailStockPlan.Label>
          <DetailStockPlan.Content> {data?.updaterUserName ?? ""}</DetailStockPlan.Content>
        </DetailStockPlan.Field>
        <DetailStockPlan.Field>
          <DetailStockPlan.Label>{t("info.updatedAt")}</DetailStockPlan.Label>
          <DetailStockPlan.Content>
            {data?.updatedAt ? timeUtil(data?.updatedAt) : ""}
          </DetailStockPlan.Content>
        </DetailStockPlan.Field>
      </DetailStockPlan>
    </>
  );
};
